import React, { useCallback, useEffect, useMemo, useState } from 'react';
/**
 * CategorizeRenderer — Assign items to categories by tapping.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { hapticManager } from '../../../utils/hapticManager';
export function CategorizeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const categories: string[] = config.categories || [];
  const items: Array<{
    text: string;
    correctCategory: number;
  }> = config.items || [];
  const [assignments, setAssignments] = useState<Record<number, number>>({});
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [liveMessage, setLiveMessage] = useState('Kies een woord en daarna een categorie.');
  const focusCategory = (index: number) => {
    document.getElementById(`categorize-category-${question.id}-${index}`)?.focus();
  };
  useEffect(() => {
    setAssignments({});
    setSelectedItem(null);
    setLiveMessage('Kies een woord en daarna een categorie.');
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(Object.keys(assignments).length === items.length && items.length > 0);
  }, [assignments, items.length, onReadyChange]);
  const answerPayload = useMemo(() => {
    // Emit a plain { itemIndex: categoryIndex } object — this is the shape
    // validateCategorize expects (it indexes userAssignments[String(idx)]).
    // Keys are sorted numerically so the payload is stable regardless of the
    // order in which the learner placed items (needed for edge-triggered submit).
    const ordered: Record<string, number> = {};
    Object.keys(assignments).map(Number).sort((a, b) => a - b).forEach(key => {
      ordered[String(key)] = assignments[key];
    });
    return JSON.stringify(ordered);
  }, [assignments]);
  const handleEdgeSubmit = useCallback((payload: string) => {
    onAnswer(payload);
    setSelectedItem(null);
  }, [onAnswer]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, handleEdgeSubmit, question.id);
  const handleItemClick = (index: number) => {
    if (shouldValidate) return;
    if (assignments[index] !== undefined) {
      const next = {
        ...assignments
      };
      delete next[index];
      setAssignments(next);
      setSelectedItem(index);
      setLiveMessage(`${items[index].text} is teruggenomen. Kies een categorie.`);
      return;
    }
    const isDeselecting = selectedItem === index;
    setSelectedItem(isDeselecting ? null : index);
    setLiveMessage(isDeselecting ? `${items[index].text} niet meer geselecteerd.` : `${items[index].text} geselecteerd. Kies nu een categorie.`);
  };
  const handleCategoryClick = (categoryIndex: number) => {
    if (shouldValidate) return;
    if (selectedItem === null) {
      setLiveMessage('Kies eerst een woord dat je wilt sorteren.');
      return;
    }
    const itemText = items[selectedItem].text;
    setAssignments(previous => ({
      ...previous,
      [selectedItem]: categoryIndex
    }));
    setSelectedItem(null);
    setLiveMessage(`${itemText} geplaatst in ${categories[categoryIndex]}.`);
    hapticManager.hapticLight();
  };
  const handleItemKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, itemIndex: number) => {
    if ((event.key === 'ArrowDown' || event.key === 'ArrowRight') && categories.length > 0) {
      event.preventDefault();
      if (selectedItem !== itemIndex) {
        setSelectedItem(itemIndex);
      }
      focusCategory(0);
      setLiveMessage(`${items[itemIndex].text} geselecteerd. Kies een categorie met de pijltjestoetsen of Enter.`);
    }
  };
  const handleCategoryKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, categoryIndex: number) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusCategory((categoryIndex + 1) % categories.length);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusCategory((categoryIndex - 1 + categories.length) % categories.length);
    }
  };
  if (categories.length === 0 || items.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-11327" data-mp="o4kdw6qwu4p8">
        <p className="text-red-700 font-bold" data-id="element-11328" data-mp="1300d0q6ckqsh">
          ⚠️ Configuratie ontbreekt (categories/items)
        </p>
      </div>;
  }
  const unassignedItems = items.map((item, index) => ({
    ...item,
    index
  })).filter(item => assignments[item.index] === undefined);
  return <div className="w-full max-w-4xl mx-auto flex flex-col gap-8" data-id="element-11329" data-mp="1lpdkkxhho6us">
      {/* Unassigned Items Pool */}
      <div className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-200 shadow-inner min-h-[120px]" data-id="element-11330" data-mp="17o9hidygbak3">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center" data-id="element-11331" data-mp="1cncklsuvf6pf">
          {unassignedItems.length > 0 ? selectedItem === null ? 'Kies een woord en daarna een categorie' : `Kies een categorie voor ${items[selectedItem].text}` : 'Alles is gesorteerd!'}
        </h3>
        <div className="flex flex-wrap justify-center gap-3" data-id="element-11332" data-mp="txxn28ee8zps">
          <AnimatePresence mode="popLayout" data-id="element-11333" data-mp="tyfqpq3dyg7q">
            {unassignedItems.map(item => {
            const isSelected = selectedItem === item.index;
            return <motion.button layout layoutId={`item-${item.index}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} key={`item-${item.index}`} onClick={() => handleItemClick(item.index)} onKeyDown={event => handleItemKeyDown(event, item.index)} disabled={shouldValidate} aria-pressed={isSelected} aria-describedby={isSelected ? `categorize-instructions-${question.id}` : undefined} className={`min-h-[44px] px-5 py-3 rounded-xl font-bold text-lg transition-all shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 ${shouldValidate ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1 hover:shadow-md'} ${isSelected ? 'text-white scale-105' : 'bg-white text-slate-700 border-2 border-slate-200'}`} style={isSelected ? {
              backgroundColor: color,
              boxShadow: `0 4px 14px ${hexToRgba(color, 0.2)}`,
              outlineColor: hexToRgba(color, 0.3),
              '--tw-ring-color': hexToRgba(color, 0.3),
              '--tw-ring-offset-width': '0px',
              '--tw-ring-offset-color': '#fff',
              '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
              '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
              boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 4px 14px rgba(0,0,0,0.1)'
            } as TailwindCSSProperties : undefined} onMouseEnter={e => {
              if (!isSelected && !shouldValidate) {
                e.currentTarget.style.borderColor = hexToRgba(color, 0.35);
              }
            }} onMouseLeave={e => {
              if (!isSelected && !shouldValidate) {
                e.currentTarget.style.borderColor = '';
              }
            }} data-id="element-11334" data-mp="jyoy5tydybaa">
                  {item.text}
                </motion.button>;
          })}
          </AnimatePresence>
          {unassignedItems.length === 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex items-center justify-center text-emerald-500 font-bold gap-2 py-2" data-id="element-11335" data-mp="37ru7czs3wbai">
              <CheckCircle2 className="w-6 h-6" data-id="element-11336" data-mp="3k83emgnb66fl" />
              <span data-id="element-11337" data-mp="36b6ji305q2sw">Klaar om te controleren</span>
            </motion.div>}
        </div>
      </div>

      {/* Categories Grid */}
      <div className={`grid gap-4 ${categories.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`} data-id="element-11338" data-mp="3ae2yljqhngs3">
        {categories.map((category, catIndex) => {
        const categoryItems = items.map((item, index) => ({
          ...item,
          index
        })).filter(item => assignments[item.index] === catIndex);
        const isTargetable = selectedItem !== null && !shouldValidate;
        return <section key={`cat-${catIndex}`} className={`flex flex-col rounded-2xl border-2 transition-all overflow-hidden min-h-[200px] ${isTargetable ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200 bg-white'} ${shouldValidate ? 'opacity-90' : ''}`} style={isTargetable ? {
          borderColor: hexToRgba(color, 0.4),
          backgroundColor: hexToRgba(color, 0.05)
        } as TailwindCSSProperties : undefined} aria-label={`Categorie: ${category}`} data-id="element-11339" data-mp="ksya6qyt8l8s" data-mpi={catIndex}>
              <button id={`categorize-category-${question.id}-${catIndex}`} type="button" onClick={() => handleCategoryClick(catIndex)} onKeyDown={event => handleCategoryKeyDown(event, catIndex)} disabled={shouldValidate} aria-label={selectedItem === null ? `Categorie ${category}. Kies eerst een woord om hier te plaatsen.` : `Plaats ${items[selectedItem].text} in categorie ${category}`} className={`min-h-[44px] p-4 text-center border-b-2 font-bold text-lg transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-indigo-400 ${isTargetable ? 'cursor-pointer' : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-50'}`} style={isTargetable ? {
            backgroundColor: hexToRgba(color, 0.1),
            borderColor: hexToRgba(color, 0.2),
            color: color
          } : undefined} data-id="element-11340" data-mp="22sly99c9hk7g">
                {category}
                <span className="block text-xs font-medium opacity-60 mt-1" data-id="element-11341" data-mp="1o6ycjjqeu7mh">
                  {categoryItems.length}{' '}
                  {categoryItems.length === 1 ? 'woord' : 'woorden'}
                </span>
              </button>
              <div className={`flex-1 p-4 flex flex-col gap-2`} style={isTargetable ? {
            backgroundColor: hexToRgba(color, 0.03)
          } : undefined} data-id="element-11342" data-mp="1x4ihp3re8o2d">
                <AnimatePresence mode="popLayout" data-id="element-11343" data-mp="18k23ycv3gry8">
                  {categoryItems.map(item => {
                let validationClass = 'bg-white border-slate-200 text-slate-700';
                let ValidationIcon = null;
                if (shouldValidate) {
                  const isCorrect = item.correctCategory === catIndex;
                  validationClass = isCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800';
                  ValidationIcon = isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-11344" data-mp="3t9rj6qzpmfrg" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0" data-id="element-11345" data-mp="3ls3br8ful2aa" />;
                }
                return <motion.button layout layoutId={`item-${item.index}`} initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} key={`item-${item.index}`} onClick={e => {
                  e.stopPropagation();
                  handleItemClick(item.index);
                }} disabled={shouldValidate} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium border-2 shadow-sm ${shouldValidate ? 'cursor-default' : 'cursor-pointer hover:border-slate-300 hover:bg-slate-50 hover:shadow'} ${validationClass}`} data-id="element-11346" data-mp="3c18frccsuobn">
                        <span className="truncate" data-id="element-11347" data-mp="3t7ew8ridrz17">{item.text}</span>
                        {ValidationIcon}
                      </motion.button>;
              })}
                </AnimatePresence>
                {!isTargetable && categoryItems.length === 0 && <div className="flex-1 flex items-center justify-center text-slate-400 text-sm font-medium border-2 border-dashed border-slate-200 rounded-xl m-2 bg-slate-50/50" data-id="element-11348" data-mp="ur2pb56f9lle">
                    Leeg
                  </div>}
              </div>
            </section>;
      })}
      </div>

      <p id={`categorize-instructions-${question.id}`} className="text-center text-sm text-slate-500" aria-live="polite" data-id="element-11349" data-mp="31kocnwnmzd5b">
        {liveMessage}
      </p>
    </div>;
}